import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useUser } from "../classes/contexts/UserContext";
import { formatDateIfDate } from "../classes/utils/DateUtils";

/**
 * Hook that provides a standardized date formatter function
 * that automatically uses the user's timezone preference and culture.
 * 
 * This is the recommended way to format dates throughout the application
 * as it ensures consistent formatting based on user preferences.
 * 
 * @returns {Function} formatDate function that takes (dateInput, age?) and returns formatted string
 * 
 * @example
 * const formatDate = useDateFormatter();
 * const formatted = formatDate("20240524"); // Uses user's timezone and culture
 * const withAge = formatDate("20240524", "Age"); // Includes age calculation
 */
export function useDateFormatter() {
  const culture = useSelector(state => state.i18n.culture);
  const { userPreferences } = useUser();
  const timezone = userPreferences?.display_settings?.timezone || "UTC";

  return useMemo(() => {
    return (input, age = undefined) => {
      return formatDateIfDate(input, culture, age, timezone);
    };
  }, [timezone, culture]);
}

